import type { RasterLayerSource } from '@/hooks/useDatasets';
import { releaseTilesPath } from './releaseTiles';
import type { LayerConfig } from './types';
import { LayerType } from './types';
import { TileLayer } from '@deck.gl/geo-layers';
import { BitmapLayer } from '@deck.gl/layers';
import { getAppConfig } from '@/app/config';
import { useEffect, useState } from 'react';
import { useAppSelector } from '@/app/hooks';
import { useAppFlags } from '@/hooks/useAppFlags';
import { captureException } from '@sentry/react';
import type { BackgroundPaint, RasterLayer, Style } from 'mapbox-gl';
import { getGeoBoundingBox } from '@/views/map/utils';
import {
  MAPBOX_STYLE_ID,
  MAPBOX_STYLE_URL,
  OVERVIEW_ZOOM_THRESHOLD,
} from '@/app/constants';

export const getSatelliteBaseLayer = (
  source: RasterLayerSource,
  config: LayerConfig,
  minZoom = OVERVIEW_ZOOM_THRESHOLD,
  maxZoom = 18
) => {
  return new TileLayer({
    id: 'sat-images',
    data: `${releaseTilesPath(source, 'webp')}?pixel_selection=first&resampling=nearest&tilesize=256`,
    loadOptions: {
      fetch: {
        credentials: 'include',
      },
    },
    ...config,
    minZoom,
    maxZoom,
    renderSubLayers: (props) => {
      if (props.data === null) {
        return null;
      }

      const boundingBox = getGeoBoundingBox(props);
      if (!boundingBox) {
        return null;
      }
      const { west, south, east, north } = boundingBox;

      return new BitmapLayer({
        id: props.id,
        image: props.data,
        bounds: [west, south, east, north],
        pickable: props.pickable,
        opacity: props.opacity,
        visible: props.visible,
        highlightColor: props.highlightColor,
      });
    },
  });
};

export const useCustomMapStyle = () => {
  const mapLayerConfig = useAppSelector((s) => s.mapLayer);
  const { flagEnableSatelliteBasemap } = useAppFlags();
  const [mapStyle, setMapStyle] = useState<string | Style>(MAPBOX_STYLE_URL);

  useEffect(() => {
    const loadMapStyle = async () => {
      try {
        const response = await fetch(
          `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE_ID}?access_token=${
            getAppConfig().mapboxToken
          }`
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch the map style: ${response.statusText}`
          );
        }

        const style: Style = await response.json();

        const baseLayerConfig = mapLayerConfig[LayerType.base];
        const darkMode =
          'darkMode' in baseLayerConfig ? baseLayerConfig.darkMode : false;
        const showLabels =
          'showLabels' in baseLayerConfig && baseLayerConfig.showLabels;

        const labelVisibility = showLabels ? 'visible' : 'none';
        const opacity = darkMode ? 0.1 : 1;
        const fillVisibility = darkMode ? 'none' : 'visible';

        const backgroundPaintJob: BackgroundPaint = {
          'background-color': [
            'interpolate',
            ['linear'],
            ['zoom'],
            15,
            'hsl(208, 25%, 100%)',
            16,
            darkMode ? 'hsl(167, 50%, 15%)' : 'hsl(208, 9%, 99%)',
          ],
        };

        const layers =
          style.layers?.map((layer) => {
            if (layer.type === 'symbol') {
              layer.layout = { ...layer.layout, visibility: labelVisibility };
            }

            if (layer.type === 'line' && layer['source-layer'] === 'road') {
              layer.paint = { ...layer.paint, 'line-opacity': opacity };
            }

            if (layer.type === 'fill' || layer.type === 'circle') {
              layer.layout = { visibility: fillVisibility };
            }

            if (layer.type === 'background') {
              layer.paint = backgroundPaintJob;
            }
            return layer;
          }) ?? [];

        if (!flagEnableSatelliteBasemap) {
          setMapStyle({ ...style, layers });
          return;
        }

        const satelliteLayer: RasterLayer = {
          id: 'mapbox-satellite',
          type: 'raster',
          source: 'mapbox-satellite',
          minzoom: OVERVIEW_ZOOM_THRESHOLD,
          layout: {
            visibility: baseLayerConfig.visible ? 'visible' : 'none',
          },
        };

        // Insert the satellite basemap below the symbol(text labels) layers so the labels stay above the imagery.
        const firstSymbolIndex = layers.findIndex((l) => l.type === 'symbol');
        const insertAt =
          firstSymbolIndex === -1 ? layers.length : firstSymbolIndex;

        setMapStyle({
          ...style,
          sources: {
            ...style.sources,
            'mapbox-satellite': {
              type: 'raster',
              url: 'mapbox://mapbox.satellite',
              tileSize: 256,
            },
          },
          layers: [
            ...layers.slice(0, insertAt),
            satelliteLayer,
            ...layers.slice(insertAt),
          ],
        });
      } catch (e) {
        captureException(e);
      }
    };

    loadMapStyle();
  }, [mapLayerConfig, flagEnableSatelliteBasemap]);

  return mapStyle;
};
